import { type ThunkDispatch, type UnknownAction } from '@reduxjs/toolkit'
import { type BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { createApi, retry } from '@reduxjs/toolkit/query/react'
import { type AxiosRequestConfig, type AxiosError } from 'axios'
import { format, parseISO } from 'date-fns'
import { toast } from 'sonner'
import config from '@/config'
import type {
  EmailEventResponseType,
  EmailEventTypes,
  EmailOrderingDirection,
  EmailOrderingType,
  ITagResponse,
  MarketingCampaignEmail,
  MarketingCampaignError,
  MarketingCampaignEvent,
  MarketingCampaignLinkType,
  MarketingCampaignStatus,
  MarketingEmailCampaignReport,
  MarketingEmailCampaignType,
  MarketingSmsCampaignError,
  MarketingSMSCampaignEvent,
  MarketingSMSCampaignMessage,
  MarketingSMSCampaignReport,
  MarketingSmsCampaignType,
  PartialCampaignType,
  SmsEventTypes,
} from '@/state/entities/marketing/campaigns/campaigns.types'
import {
  type RowsOverview,
  type VoucherCodeImportRowsType,
  type VoucherCodeImportType,
  type VoucherCodeType,
  type VoucherPerformanceReportType,
  type VoucherStatusType,
  type VoucherType,
  type VoucherCodeResponseType,
} from '@/state/entities/vouchers/vouchers.types'
import {
  type DomainType,
  type EmailAddressType,
} from '@/state/entities/marketing/domains/domains.types'
import {
  type EmailEventType,
  type EmailType,
} from '@/state/entities/marketing/emails/emails.types'
import {
  type RevisionOwnerType,
  type RevisionType,
  type SimpleRevisionType,
} from '@/state/entities/marketing/revisions/revisions.types'
import type { ServiceSettingsType } from '@/state/entities/marketing/service-settings/service-settings.types'
import {
  type SmsSenderType,
  type SmsType,
  type SmsSendersResponseType,
} from '@/state/entities/marketing/sms/sms.types'
import {
  type MarketingEmailTemplateType,
  type ParentEmailTemplate,
  type PartialMarketingEmailTemplateType,
  type SimpleParentEmailTemplate,
  type TemplatePresetType,
} from '@/state/entities/marketing/templates/templates.types'
import {
  type ServiceCursorPaginatedResponseType,
  type ServicePaginatedResponseType,
  ServiceErrorHandler,
} from '@/connect-types/backend/service'
import { customAxios } from '@/utils/axiosHelper'
import { buildUrl } from '@/utils/common'
import { TimeFormats } from '@/common/datepicker/utilities/formats'
import { post } from '@/utils/api'
import { type VerificationEmail } from '@/pages/[org_id]/marketing/email/verification-emails/types'
import { shouldBailout } from '../helpers'
import {
  type WorkflowTriggerCondition,
  type WorkflowEmail,
  type WorkflowNodeType,
  type WorkflowType,
  type WorkflowRun,
  type WorkflowRunSimple,
  type WorkflowTrigger,
  type WorkflowMetadata,
  type WorkflowLog,
  type WorkflowSmsMessage,
  type CreateWorkflowSmsMessageRequest,
  type UpdateWorkflowSmsMessageRequest,
  type WorkflowEmailSimple,
  type WorkflowSeeder,
  type WorkflowSeederLog,
  type WorkflowSeederResult,
  type WorkflowTemplate,
  type WorkflowTemplateSimple,
  type InstantiateWorkflowFromTemplateRequest,
} from '../entities/workflows/workflows.types'
import type { SegmentDataExport } from '../entities/marketing/segment-data-exports/segment-data-exports.types'
import morpheusApi from '../morpheus/morpheus.slice'
import {
  type EmailMarketingReportType,
  type TagResourceType,
  type UserEmailEvent,
} from './types/email.marketing'

interface SupportedDataProvidersResponse {
  data_providers: string[]
  data_providers_meta: Record<string, { label: string; description: string }>
}

// Tracks in-flight POST /reports calls keyed by args so concurrent
// transformErrorResponse runs (e.g. two useReportBuilder mounts, or a
// pusher-driven refetch storm) coalesce into a single backend create.
const inflightReportCreates = new Map<
  string,
  Promise<{ data: EmailMarketingReportType }>
>()

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
  }> =>
  async ({ url, method, data, params }) => {
    try {
      const result = await customAxios({
        url: baseUrl + url,
        method,
        data,
        params,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if ((method ?? '').toUpperCase() !== 'GET') {
        ServiceErrorHandler(axiosError)
      }

      if (shouldBailout(err)) {
        retry.fail(err)
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

/**
 * A workflow trigger mutation also makes morpheus' cached workflow stale, so
 * invalidate it once the request lands. A rejected request changed nothing
 * (and its error already reaches the caller through the mutation result), so
 * there is nothing to invalidate in that case.
 */
async function invalidateMorpheusWorkflow(
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  workflowId: string,
  queryFulfilled: Promise<unknown>
): Promise<void> {
  const succeeded = await queryFulfilled.then(
    () => true,
    () => false
  )
  if (succeeded) {
    dispatch(
      morpheusApi.util.invalidateTags([{ type: 'Workflows', id: workflowId }])
    )
  }
}

const serviceApi = createApi({
  keepUnusedDataFor: 60 * 60 * 24,
  reducerPath: 'service',
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.service }), {
    maxRetries: 5,
  }),

  tagTypes: [
    'PresetEmailTemplates',
    'EmailMarketingCampaigns',
    'EmailMarketingTemplates',
    'ParentEmailMarketingTemplates',
    'EmailMarketingRevisions',
    'EmailMarketingCampaignReport',
    'EmailDomains',
    'EmailAddresses',
    'SMSMessages',
    'SMSSenders',
    'SMSCampaigns',
    'SmsLedger',
    'ServiceSettings',
    'Vouchers',
    'ServiceTags',
    'Workflow',
    'WorkflowEmail',
    'WorkflowRun',
    'WorkflowSmsMessage',
    'WorkflowSmsMessages',
    'WorkflowLogs',
    'SegmentDataExports',
    'SupportedDataProviders',
  ],

  endpoints: (build) => ({
    getScheduleBusyTimes: build.query<
      { date: Date; busyness: number }[],
      { date: Date }
    >({
      query: ({ date }) => ({
        url: buildUrl(`/general/email-campaigns/schedule-busy-times`, {
          date: date.toISOString(),
        }),
        method: 'GET',
      }),
      transformResponse: (
        res: {
          date: string
          busyness: number
        }[]
      ) => {
        return res.map(({ date, busyness }) => ({
          date: parseISO(date),
          busyness,
        }))
      },
    }),
    getTags: build.query<
      ITagResponse<{ name: string; count: number }>,
      {
        orgId: string
        resource_type: TagResourceType
        name_like?: string
      }
    >({
      query: ({ orgId, resource_type = 'EmailCampaign', name_like = '' }) => ({
        url: buildUrl(`/${orgId}/tags/distinct-by-resource-type`, {
          resource_type,
          name_like,
        }),
        method: 'GET',
      }),
      providesTags: (result: any) => [{ type: 'ServiceTags', id: 'LIST' }],
    }),
    deleteTag: build.mutation<
      { message: string },
      { orgId: string; resource_type: TagResourceType; tagId: string }
    >({
      query: ({ orgId, resource_type = 'EmailCampaign', tagId }) => ({
        url: buildUrl(`/${orgId}/tags/${tagId}`, {
          resource_type,
        }),
        method: 'DELETE',
      }),
      invalidatesTags: (result: any) => [
        { type: 'ServiceTags', id: 'LIST' },
        { type: 'EmailMarketingCampaigns', id: 'LIST' },
        { type: 'EmailMarketingTemplates', id: 'LIST' },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
    }),
    createTag: build.mutation<
      { message: string },
      {
        orgId: string
        resource_type: TagResourceType
        name: string
        value: string | null | undefined
        resource_id: string
      }
    >({
      query: ({
        orgId,
        resource_type = 'EmailCampaign',
        name,
        value,
        resource_id,
      }) => ({
        url: `/${orgId}/tags`,
        method: 'POST',
        data: {
          resource_type,
          name,
          ...(value && { value }),
          resource_id,
        },
      }),
      invalidatesTags: (result: any) => [
        { type: 'ServiceTags', id: 'LIST' },

        { type: 'EmailMarketingCampaigns', id: 'LIST' },
        { type: 'EmailMarketingTemplates', id: 'LIST' },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
    }),
    presetTemplates: build.query<Omit<TemplatePresetType, 'design'>[], unknown>(
      {
        query: () => ({
          url: `/unlayer-templates`,
          method: 'get',
        }),
        providesTags: ['PresetEmailTemplates'],
      }
    ),
    presetTemplate: build.query<TemplatePresetType, number>({
      query: (id: any) => ({
        url: `/unlayer-templates/${id}`,
        method: 'get',
      }),
    }),

    getVerificationEmails: build.query<
      ServiceCursorPaginatedResponseType<VerificationEmail>,
      { orgId: string; cursor: string; limit?: number }
    >({
      query: ({ orgId, cursor, limit }) => ({
        url: buildUrl(`/${orgId}/verification-emails`, {
          cursor,
          query_type: 'cursor',
          limit,
        }),
        method: 'get',
      }),
    }),

    getAuditEmails: build.query<
      ServiceCursorPaginatedResponseType<EmailType>,
      {
        orgId: string
        to_email: string
        cursor?: string | null | undefined
        limit?: number | null
      }
    >({
      query: ({ orgId, to_email, cursor, limit }) => ({
        url: buildUrl(`/${orgId}/emails`, {
          to_email,
          cursor: cursor ?? null,
          limit: limit ?? 25,
        }),
        method: 'get',
      }),
    }),

    getAuditEmail: build.query<
      EmailType,
      {
        orgId: string
        email_id: string
      }
    >({
      query: ({ orgId, email_id }) => ({
        url: `/${orgId}/emails/${email_id}`,
        method: 'get',
      }),
      transformResponse: (response: { data: EmailType }) => {
        return response.data
      },
    }),
    getAuditEmailEvents: build.query<
      ServicePaginatedResponseType<EmailEventType>,
      {
        orgId: string
        email_id: string
        page: number
      }
    >({
      query: ({ orgId, email_id, page }) => ({
        url: `/${orgId}/emails/${email_id}/events?page=${page}`,
        method: 'get',
      }),
    }),

    getEmailMarketingCampaigns: build.query<
      ServicePaginatedResponseType<PartialCampaignType>,
      {
        orgId: string
        name?: string
        searchBy?: 'name' | 'tag'
        page: number
        order_by?: EmailOrderingType
        order_by_direction?: EmailOrderingDirection
        status_in?: MarketingCampaignStatus[] | undefined
        tag_name?: string
        is_automated?: boolean
      }
    >({
      query: ({ orgId, ...rest }) => {
        const {
          name,
          searchBy,
          page,
          order_by,
          order_by_direction,
          status_in,
          tag_name,
          is_automated,
        } = rest
        const isTag = searchBy === 'tag'
        let tag_value: string, hasTagValue: boolean

        if (name && name !== '') {
          hasTagValue = name.includes(':')
          tag_value = isTag && hasTagValue ? name.split(':')[1].trim() : null
        }
        const url = buildUrl(`/${orgId}/marketing/email-campaigns`, {
          name: name && isTag ? null : name,
          has_tag_name: tag_name,
          has_tag_value: name && isTag ? tag_value : null,
          page,
          order_by,
          order_by_direction,
          status_in,
          is_automated:
            typeof is_automated === 'boolean' ? (is_automated ? 1 : 0) : null,
        })

        return {
          url,
          method: 'get',
        }
      },
      providesTags: (items: any) => [
        ...(items?.data?.map((item: any) => ({
          id: item?.id,
          type: 'EmailMarketingCampaigns' as const,
        })) ?? []),
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    getMarketingEmailCampaign: build.query<
      MarketingEmailCampaignType,
      { orgId: string; campaignId: string }
    >({
      query: ({ orgId, campaignId }) => ({
        url: `/${orgId}/marketing/email-campaigns/${campaignId}`,
        method: 'get',
      }),
      transformResponse: (res: {
        email_campaign: MarketingEmailCampaignType
      }) => {
        return res.email_campaign
      },
      transformErrorResponse: (error: any) => {
        return error
      },
      providesTags: (item: any) => [
        { id: item?.id, type: 'EmailMarketingCampaigns' },
      ],
    }),
    updateMarketingEmailCampaign: build.mutation<
      MarketingEmailCampaignType,
      MarketingEmailCampaignType
    >({
      query: (campaign: any) => {
        const camp =
          campaign.segment_id === 'all' || campaign.segment_id === ''
            ? { ...campaign, segment_id: null }
            : campaign
        return {
          url: `/${campaign.organisation_id}/marketing/email-campaigns/${campaign.id}`,
          method: 'PUT',
          data: camp,
        }
      },
      transformResponse: (res: {
        email_campaign: MarketingEmailCampaignType
      }) => {
        return res.email_campaign
      },
      invalidatesTags: (item: any) =>
        item ? [{ id: item?.id, type: 'EmailMarketingCampaigns' }] : [],
    }),
    createMarketingEmailCampaign: build.mutation<
      MarketingEmailCampaignType,
      Partial<MarketingEmailCampaignType>
    >({
      query: (campaign: any) => ({
        url: `/${campaign.organisation_id}/marketing/email-campaigns`,
        method: 'post',
        data: {
          ...campaign,
          spend_per_head_currency: campaign.spend_per_head_currency ?? 'GBP',
        },
      }),
      transformResponse: (res: {
        email_campaign: MarketingEmailCampaignType
      }) => {
        toast.success(`${res.email_campaign.name} created`)
        return res.email_campaign
      },
      invalidatesTags: () => [{ id: 'LIST', type: 'EmailMarketingCampaigns' }],
    }),
    deleteMarketingEmailCampaign: build.mutation<unknown, PartialCampaignType>({
      query: (campaign: any) => ({
        url: `/${campaign.organisation_id}/marketing/email-campaigns/${campaign.id}`,
        method: 'delete',
      }),
      transformResponse: (res, _, { name }) => {
        toast.success(`${name} deleted`)
        return res
      },
      invalidatesTags: () => [{ id: 'LIST', type: 'EmailMarketingCampaigns' }],
    }),

    /**
     *
     */
    requestApprovalFormMarketingEmailCampaign: build.mutation<
      { message: string },
      PartialCampaignType
    >({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/request-approval`,
        method: 'post',
      }),
      transformResponse: (res: { message: string }) => {
        toast.success(res.message)
        return res
      },
      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    approvalFormMarketingEmailCampaign: build.mutation<
      { message: string },
      PartialCampaignType
    >({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/approve-campaign`,
        method: 'post',
      }),
      transformResponse: (res: { message: string }) => {
        toast.success(res.message)
        return res
      },
      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),

    sendMarketingEmailTemplatePreview: build.mutation<
      { message: string; text: string },
      {
        organisation_id: string
        id: string
        from_id: string
        to_email: string
        to_name: string
      }
    >({
      query: ({ organisation_id, id, from_id, to_email, to_name }) => ({
        url: `/${organisation_id}/marketing/email-templates/${id}/send-preview`,
        data: {
          from_id,
          to_email,
          to_name,
        },
        method: 'post',
      }),
    }),

    sendMarketingEmailCampaignPreview: build.mutation<
      { message: string; text: string },
      {
        organisation_id: string
        id: string
        /**
         * Verified email-address id to send from. When omitted (or
         * an empty string) the service falls back to its default
         * Stampede sender — used when an org has no verified
         * addresses yet, or the user picks "Stampede default".
         */
        from_id?: string
        to_email: string
        to_name: string
      }
    >({
      query: ({ organisation_id, id, from_id, to_email, to_name }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/send-preview`,
        data: {
          // Only include `from_id` when set so the wire payload is
          // `{ to_email, to_name }` rather than `{ from_id: "", ... }`
          // — service handles a missing key as the Stampede default.
          ...(from_id ? { from_id } : {}),
          to_email,
          to_name,
        },
        method: 'post',
      }),
    }),

    sendMarketingEmailCampaign: build.mutation<unknown, PartialCampaignType>({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/send-campaign`,
        method: 'post',
      }),
      transformResponse: (res, _2, { name }) => {
        toast.success(`Campaign ${name} is sending`)
        return res
      },
      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    scheduleMarketingEmailCampaign: build.mutation<
      unknown,
      MarketingEmailCampaignType
    >({
      query: ({ organisation_id, id, scheduled_to_send_at }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/schedule-campaign`,
        method: 'post',
        data: { scheduled_to_send_at },
      }),

      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    cancelScheduleMarketingEmailCampaign: build.mutation<
      unknown,
      PartialCampaignType
    >({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/cancel-scheduled-campaign`,
        method: 'post',
        data: {},
      }),
      transformResponse: (res: any) => {
        toast.success('Campaign schedule cancelled')
        return res
      },
      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    cancelMarketingEmailCampaign: build.mutation<unknown, PartialCampaignType>({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/cancel-campaign`,
        method: 'post',
        data: {},
      }),
      transformResponse: (res: any) => {
        toast.success('Campaign cancelled')
        return res
      },
      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    startMarketingEmailAutomationCampaign: build.mutation<
      unknown,
      PartialCampaignType
    >({
      query: ({ organisation_id, id, started_at }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/start-automated-campaign`,
        method: 'post',
        data: { started_at },
      }),
      transformResponse: (res: any) => {
        toast.success('Automation campaign started')
        return res
      },
      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    stopMarketingEmailAutomationCampaign: build.mutation<
      unknown,
      PartialCampaignType
    >({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-campaigns/${id}/pause-automated-campaign`,
        method: 'post',
        data: {},
      }),
      transformResponse: (res: any) => {
        toast.success('Automation campaign stopped')
        return res
      },
      invalidatesTags: (_unused, _2, { id }) => [
        { id, type: 'EmailMarketingCampaigns' },
        { id: 'LIST', type: 'EmailMarketingCampaigns' },
      ],
    }),
    getMarketingEmailCampaignReport: build.query<
      MarketingEmailCampaignReport,
      { orgId: string; campaignId: string }
    >({
      query: ({ orgId, campaignId }) => ({
        url: `/${orgId}/marketing/email-campaigns/${campaignId}/reports`,
        method: 'get',
      }),
    }),
    getEmailCampaignDailyStats: build.query<
      {
        data: { date: string; send: number; open: number; click: number }[]
        totals: {
          send: number
          open: number
          click: number
          open_rate: number
          click_rate: number
        }
      },
      { orgId: string; from: string; to: string }
    >({
      query: ({ orgId, from, to }) => ({
        url: buildUrl(`/${orgId}/marketing/email-campaigns/daily-stats`, {
          from,
          to,
        }),
        method: 'get',
      }),
    }),
    getMarketingEmailCampaignErrorReport: build.query<
      ServicePaginatedResponseType<MarketingCampaignError>,
      {
        orgId: string
        campaign_id: string
        page: number
      }
    >({
      query: ({ orgId, campaign_id, page }) => ({
        url: buildUrl(
          `/${orgId}/marketing/email-campaigns/${campaign_id}/errors`,
          { page }
        ),
        method: 'get',
      }),
    }),
    getMarketingEmailCampaignReportLinks: build.query<
      MarketingCampaignLinkType[],
      { orgId: string; campaignId: string }
    >({
      query: ({ orgId, campaignId }) => ({
        url: `/${orgId}/marketing/email-campaigns/${campaignId}/clicked-links`,
        method: 'get',
      }),
      transformResponse: (res: {
        clicked_links: MarketingCampaignLinkType[]
      }) => {
        return res.clicked_links.filter(
          (item: any) => !item?.click_url.startsWith('https://stampede.ai')
        )
      },
    }),
    getMarketingEmailCampaignSentTo: build.query<
      ServiceCursorPaginatedResponseType<MarketingCampaignEmail>,
      { orgId: string; campaignId: string; page: string; limit: number }
    >({
      query: ({ orgId, campaignId, limit, page }) => ({
        url: buildUrl(
          `/${orgId}/marketing/email-campaigns/${campaignId}/emails`,
          { cursor: page, limit, query_type: 'cursor' }
        ),
        method: 'get',
      }),
    }),
    getMarketingEmailCampaignEventTypes: build.query<
      EmailEventResponseType,
      { orgId: string; campaignId: string }
    >({
      query: ({ orgId, campaignId }) => ({
        url: `/${orgId}/marketing/email-campaigns/${campaignId}/event-types`,
        method: 'get',
      }),
      transformResponse: (res: { event_types: EmailEventResponseType }) => {
        return res.event_types
      },
    }),
    getMarketingEmailCampaignEvents: build.query<
      ServiceCursorPaginatedResponseType<MarketingCampaignEvent>,
      {
        orgId: string
        campaignId: string
        page: string
        limit?: number
        event_type?: EmailEventTypes
      }
    >({
      query: ({ orgId, campaignId, ...rest }) => ({
        url: buildUrl(
          `/${orgId}/marketing/email-campaigns/${campaignId}/events`,
          { ...rest, query_type: 'cursor' }
        ),
        method: 'get',
      }),
    }),

    getEmailMarketingTemplates: build.query<
      ServicePaginatedResponseType<PartialMarketingEmailTemplateType>,
      { orgId: string; page: number; tag_name?: string }
    >({
      query: ({ orgId, page, tag_name }) => ({
        url: buildUrl(`/${orgId}/marketing/email-templates`, {
          page,
          ...(tag_name && { has_tag_name: tag_name }),
        }),
        method: 'get',
      }),
      providesTags: (items: any) => [
        ...(items?.data?.map((item: any) => ({
          id: item?.id,
          type: 'EmailMarketingTemplates' as const,
        })) ?? []),
        { id: 'LIST', type: 'EmailMarketingTemplates' },
      ],
    }),
    getParentEmailMarketingTemplates: build.query<
      ServicePaginatedResponseType<SimpleParentEmailTemplate>,
      { orgId: string; page?: number; tag_name?: string }
    >({
      query: ({ orgId, page, tag_name }) => ({
        url: buildUrl(`/${orgId}/marketing/parent-email-templates`, {
          page: page ?? 1,
          ...(tag_name && { has_tag_name: tag_name }),
        }),
        method: 'get',
      }),
      providesTags: (items: any) => [
        ...(items?.data?.map((item: any) => ({
          id: item?.id,
          type: 'ParentEmailMarketingTemplates' as const,
        })) ?? []),
        { id: 'LIST', type: 'ParentEmailMarketingTemplates' },
      ],
    }),
    getParentEmailMarketingTemplate: build.query<
      ParentEmailTemplate,
      { orgId: string; templateId: string }
    >({
      query: ({ orgId, templateId }) => ({
        url: `/${orgId}/marketing/parent-email-templates/${templateId}`,
        method: 'get',
      }),
      providesTags: (item: any) => [
        { id: item?.id, type: 'ParentEmailMarketingTemplates' },
      ],
      transformResponse: (item: { email_template: ParentEmailTemplate }) => {
        return item?.email_template
      },
    }),
    getEmailMarketingTemplate: build.query<
      MarketingEmailTemplateType,
      { orgId: string; templateId: string }
    >({
      query: ({ orgId, templateId }) => ({
        url: `/${orgId}/marketing/email-templates/${templateId}`,
        method: 'get',
      }),
      providesTags: (item: any) => [
        { id: item?.id, type: 'EmailMarketingTemplates' },
      ],
      transformResponse: (item: {
        email_template: MarketingEmailTemplateType
      }) => {
        return item?.email_template
      },
    }),
    createEmailMarketingTemplate: build.mutation<
      MarketingEmailTemplateType,
      { orgId: string; template: Partial<MarketingEmailTemplateType> }
    >({
      query: ({ orgId, template }) => ({
        url: `/${orgId}/marketing/email-templates`,
        method: 'post',
        data: template,
      }),
      invalidatesTags: [{ id: 'LIST', type: 'EmailMarketingTemplates' }],
      transformResponse: (item: {
        email_template: MarketingEmailTemplateType
      }) => {
        return item?.email_template
      },
    }),
    updateEmailMarketingTemplate: build.mutation<
      MarketingEmailTemplateType,
      { orgId: string; template: MarketingEmailTemplateType }
    >({
      query: ({ orgId, template }) => ({
        url: `/${orgId}/marketing/email-templates/${template.id}`,
        method: 'put',
        data: template,
      }),
      invalidatesTags: (arg: any) => [
        { id: arg.id, type: 'EmailMarketingTemplates' },
      ],
      transformResponse: (item: {
        email_template: MarketingEmailTemplateType
      }) => {
        return item?.email_template
      },
    }),
    deleteEmailMarketingTemplate: build.mutation<
      unknown,
      { orgId: string; template: PartialMarketingEmailTemplateType }
    >({
      query: ({ orgId, template }) => ({
        url: `/${orgId}/marketing/email-templates/${template.id}`,
        method: 'delete',
        data: template,
      }),
      invalidatesTags: [{ id: 'LIST', type: 'EmailMarketingTemplates' }],
      transformResponse: (item: unknown, _, { template }) => {
        return item
      },
    }),
    getEmailMarketingRevisions: build.query<
      ServicePaginatedResponseType<SimpleRevisionType>,
      {
        orgId: string
        owner_type: RevisionOwnerType
        owner_id?: string
        page: number // look at building a ServicePaginatedRequestType
      }
    >({
      query: ({ orgId, ...rest }) => ({
        url: buildUrl(`/${orgId}/revisions`, rest),
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'EmailMarketingRevisions' }],
    }),
    getEmailMarketingRevision: build.query<
      RevisionType,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/revisions/${id}`,
        method: 'get',
      }),
      providesTags: (item: any) => [
        { id: item?.id, type: 'EmailMarketingRevisions' },
      ],
    }),
    getEmailDomains: build.query<
      ServicePaginatedResponseType<DomainType>,
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/marketing/domains`,
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'EmailDomains' }],
    }),
    getEmailDomain: build.query<
      DomainType,
      {
        orgId: string
        domain_id: string
      }
    >({
      query: ({ orgId, domain_id }) => ({
        url: `/${orgId}/marketing/domains/${domain_id}`,
        method: 'get',
      }),
      transformResponse: (item: { domain: DomainType }) => {
        return item?.domain
      },
      providesTags: ({ id }) => [{ id, type: 'EmailDomains' }],
    }),

    getEmailAddresses: build.query<
      ServicePaginatedResponseType<EmailAddressType>,
      {
        orgId: string
        page: number
        domain_id?: string
      }
    >({
      query: ({ orgId, domain_id }) => ({
        url: buildUrl(`/${orgId}/marketing/email-addresses`, {
          domain_id,
          limit: 50,
        }),
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'EmailAddresses' }],
    }),
    getEmailAddress: build.query<
      EmailAddressType,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/marketing/email-addresses/${id}`,
        method: 'get',
      }),
      providesTags: (item: any) =>
        item ? [{ id: item?.id, type: 'EmailAddresses' }] : [],
      transformResponse: (item: { email_address: EmailAddressType }) => {
        return item?.email_address
      },
    }),
    deleteEmailAddress: build.mutation<unknown, EmailAddressType>({
      query: ({ id, organisation_id }) => ({
        url: `/${organisation_id}/marketing/email-addresses/${id}`,
        method: 'delete',
      }),
      invalidatesTags: () => [
        { id: 'LIST', type: 'EmailAddresses' },
        { id: 'LIST', type: 'EmailDomains' },
      ],
    }),
    createEmailAddress: build.mutation<
      EmailAddressType,
      {
        orgId: string
        email: string
        name: string
      }
    >({
      query: ({ orgId, name, email }) => ({
        url: `/${orgId}/marketing/email-addresses`,
        method: 'post',
        data: { email, name },
      }),
      invalidatesTags: () => [
        { id: 'LIST', type: 'EmailAddresses' },
        { id: 'LIST', type: 'EmailDomains' },
      ],
      transformResponse: (item: { email_address: EmailAddressType }) => {
        toast.success('Email address created')
        return item?.email_address
      },
    }),
    updateEmailAddress: build.mutation<EmailAddressType, EmailAddressType>({
      query: ({ organisation_id, name, id }) => ({
        url: `/${organisation_id}/marketing/email-addresses/${id}`,
        method: 'put',
        data: { name },
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'EmailAddresses' }],
      transformResponse: (item: { email_address: EmailAddressType }) => {
        toast.success('Email address updated')
        return item?.email_address
      },
    }),
    updatePrimaryEmailAddress: build.mutation<
      EmailAddressType,
      EmailAddressType
    >({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-addresses/${id}/make-primary`,
        method: 'put',
        data: {},
      }),
      invalidatesTags: (_item: any) => [
        { id: 'LIST', type: 'EmailAddresses' },
        { id: 'LIST', type: 'EmailDomains' },
      ],
      transformResponse: (item: { email_address: EmailAddressType }) => {
        toast.success('Email address moved to primary')
        return item?.email_address
      },
    }),
    resendValidationEmailAddress: build.mutation<
      EmailAddressType,
      EmailAddressType
    >({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-addresses/${id}/resend-verification-email`,
        method: 'put',
        data: {},
      }),
      invalidatesTags: (item: any) => [
        { id: item?.id, type: 'EmailAddresses' },
      ],
      transformResponse: (item: { email_address: EmailAddressType }) => {
        toast.success(`Validation email sent to ${item?.email_address.email}`)
        return item?.email_address
      },
    }),
    refreshEmailDomain: build.mutation<DomainType, DomainType>({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/domains/${id}/refresh`,
        method: 'put',
        data: {},
      }),
      invalidatesTags: (_item: any) => [
        { id: 'LIST', type: 'EmailAddresses' },
        { id: 'LIST', type: 'EmailDomains' },
      ],
      transformResponse: (item: { domain: DomainType }) => {
        toast.success(`DNS status updated ${item?.domain.name}`)
        return item?.domain
      },
    }),
    refreshEmailAddress: build.mutation<EmailAddressType, EmailAddressType>({
      query: ({ organisation_id, id }) => ({
        url: `/${organisation_id}/marketing/email-addresses/${id}/refresh`,
        method: 'put',
        data: {},
      }),
      invalidatesTags: (_item: any) => [
        { id: 'LIST', type: 'EmailAddresses' },
        { id: 'LIST', type: 'EmailDomains' },
      ],
      transformResponse: (item: { email_address: EmailAddressType }) => {
        return item?.email_address
      },
    }),
    getUserEmailEvents: build.query<
      ServicePaginatedResponseType<UserEmailEvent>,
      {
        orgId: string
        email: string
        cursor: string | null | undefined
      }
    >({
      query: ({ orgId, email, cursor }) => {
        return {
          url: buildUrl(`/${orgId}/email-events/for-email`, {
            email,
            cursor,
          }),
          method: 'get',
        }
      },
    }),

    /*
    SMS START
    */

    getSmsMessages: build.query<
      ServicePaginatedResponseType<SmsType>,
      { orgId: string; limit?: number; page: string; to_contains?: string }
    >({
      query: ({ orgId, to_contains, page, limit }) => ({
        url: buildUrl(`/${orgId}/marketing/sms-messages`, {
          cursor: to_contains !== '' ? null : page,
          to_contains,
          query_type: 'cursor',
          limit,
        }),
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'SMSMessages' }],
    }),

    getSmsSenders: build.query<
      SmsSendersResponseType,
      { orgId: string; page: number }
    >({
      query: ({ orgId, page }) => ({
        url: buildUrl(`/${orgId}/marketing/sms-senders`, {
          page,
        }),
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'SMSSenders' }],
    }),

    getSmsLedger: build.query<
      ServicePaginatedResponseType<any>,
      { orgId: string; cursor?: string; limit?: number }
    >({
      query: ({ orgId, cursor, limit = 25 }) => ({
        url: buildUrl(`/${orgId}/sms-ledger`, {
          cursor,
          query_type: 'cursor',
          limit,
        }),
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'SmsLedger' }],
    }),

    getSmsSender: build.query<SmsSenderType, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/marketing/sms-senders/${id}`,
        method: 'get',
      }),
      providesTags: (item: any) => [{ id: item?.id, type: 'SMSSenders' }],
      transformResponse: (item: { sms_senders: SmsSenderType }) => {
        return item?.sms_senders
      },
    }),
    updateSmsSender: build.mutation<SmsSenderType, SmsSenderType>({
      query: ({ organisation_id, ...sender }) => ({
        url: `/${organisation_id}/marketing/sms-senders/${sender.id}`,
        method: 'put',
        data: sender,
      }),
      invalidatesTags: (item: any) => [
        { id: item?.id, type: 'SMSSenders' },
        { id: 'LIST', type: 'SMSSenders' },
      ],
      transformResponse: (item: { sms_sender: SmsSenderType }) => {
        toast.success('SMS sender updated')
        return item?.sms_sender
      },
    }),
    createSmsSender: build.mutation<SmsSenderType, Partial<SmsSenderType>>({
      query: ({ organisation_id, ...sender }) => ({
        url: `/${organisation_id}/marketing/sms-senders`,
        method: 'post',
        data: sender,
      }),
      invalidatesTags: (item: any) => [
        { id: item?.id, type: 'SMSSenders' },
        { id: 'LIST', type: 'SMSSenders' },
      ],
      transformResponse: (item: { sms_sender: SmsSenderType }) => {
        toast.success('SMS sender created')
        return item?.sms_sender
      },
    }),
    deleteSmsSender: build.mutation<unknown, SmsSenderType>({
      query: ({ organisation_id, ...sender }) => ({
        url: `/${organisation_id}/marketing/sms-senders/${sender.id}`,
        method: 'delete',
        data: sender,
      }),
      invalidatesTags: (_un1, _un2, item) => [
        { id: item?.id, type: 'SMSSenders' },
        { id: 'LIST', type: 'SMSSenders' },
      ],
      transformResponse: (item: unknown) => {
        toast.success('SMS sender deleted')
        return item
      },
    }),

    getSmsMarketingReport: build.query<
      MarketingSMSCampaignReport,
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/marketing/sms-campaigns/reports`,
        method: 'get',
      }),
    }),

    getSmsMarketingCampaign: build.query<
      MarketingSmsCampaignType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/marketing/sms-campaigns/${id}`,
        method: 'get',
      }),
      providesTags: (item: any) =>
        item ? [{ type: 'SMSCampaigns', id: item?.id }] : [],
      transformResponse: (item: { sms_campaign: MarketingSmsCampaignType }) => {
        return item?.sms_campaign
      },
    }),
    getSmsMarketingCampaigns: build.query<
      ServicePaginatedResponseType<MarketingSmsCampaignType>,
      { orgId: string; page: number; name?: string }
    >({
      query: (props: any) => {
        const { orgId, name = '', page } = props

        const tagValueCheck = name.includes(':')
        const has_tag_name = !tagValueCheck ? name : name.split(':')[0].trim()
        const has_tag_value = tagValueCheck ? name.split(':')[1].trim() : null

        return {
          method: 'get',
          url: buildUrl(`/${orgId}/marketing/sms-campaigns`, {
            page: !name ? page : null,
            has_tag_name,
            has_tag_value,
          }),
        }
      },
      providesTags: () => [{ type: 'SMSCampaigns', id: 'LIST' }],
    }),
    createSmsMarketingCampaign: build.mutation<
      MarketingSmsCampaignType,
      Partial<MarketingSmsCampaignType>
    >({
      query: ({ organisation_id, ...rest }) => ({
        url: `/${organisation_id}/marketing/sms-campaigns`,
        method: 'post',
        data: {
          ...rest,
          spend_per_head_currency: rest.spend_per_head_currency ?? 'GBP',
        },
      }),
      invalidatesTags: () => [{ type: 'SMSCampaigns', id: 'LIST' }],
      transformResponse: (item: { sms_campaign: MarketingSmsCampaignType }) => {
        toast.success('SMS campaign created')
        return item?.sms_campaign
      },
    }),
    updateSmsMarketingCampaign: build.mutation<
      MarketingSmsCampaignType,
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, ...rest }) => {
        if (rest.segment_id === 'all') {
          rest.segment_id = null
        }
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}`,
          method: 'put',
          data: rest,
        }
      },
      invalidatesTags: (item: any) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: { sms_campaign: MarketingSmsCampaignType }) => {
        toast.success('SMS campaign updated')
        return item?.sms_campaign
      },
    }),
    deleteSmsMarketingCampaign: build.mutation<
      unknown,
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}`,
          method: 'delete',
        }
      },
      invalidatesTags: () => [{ type: 'SMSCampaigns', id: 'LIST' }],
      transformResponse: (item: unknown) => {
        toast.success('SMS campaign deleted')
        return item
      },
    }),
    requestApprovalSmsMarketingCampaign: build.mutation<
      { message: string },
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/request-approval`,
          method: 'post',
          data: {},
        }
      },
      invalidatesTags: (_item, _2, item) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: { message: string }) => {
        toast.success(item?.message)
        return item
      },
    }),
    approveSmsMarketingCampaign: build.mutation<
      { message: string },
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/approve-campaign`,
          method: 'post',
          data: {},
        }
      },
      invalidatesTags: (_item, _2, item) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: { message: string }) => {
        toast.success(item?.message)
        return item
      },
    }),
    sendSmsMarketingCampaign: build.mutation<unknown, MarketingSmsCampaignType>(
      {
        query: ({ organisation_id, ...rest }) => {
          return {
            url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/send-campaign`,
            method: 'post',
            data: {},
          }
        },
        invalidatesTags: (_item, _2, item) => [
          { type: 'SMSCampaigns', id: item?.id },
          { type: 'SMSCampaigns', id: 'LIST' },
        ],
        transformResponse: (item: unknown) => {
          toast.success(`SMS campaign sent`)
          return item
        },
      }
    ),
    scheduleSmsMarketingCampaign: build.mutation<
      unknown,
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, scheduled_to_send_at, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/schedule-campaign`,
          method: 'post',
          data: {
            scheduled_to_send_at,
          },
        }
      },
      invalidatesTags: (_item, _2, item) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: unknown) => {
        toast.success(`SMS campaign scheduled`)
        return item
      },
    }),
    cancelScheduleSmsMarketingCampaign: build.mutation<
      unknown,
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/cancel-scheduled-campaign`,
          method: 'post',
          data: {},
        }
      },
      invalidatesTags: (_item, _2, item) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: unknown) => {
        toast.success(`SMS campaign schedule cancelled`)
        return item
      },
    }),
    cancelSmsMarketingCampaign: build.mutation<
      unknown,
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/cancel-campaign`,
          method: 'post',
          data: {},
        }
      },
      invalidatesTags: (_item, _2, item) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: unknown) => {
        toast.success(`SMS campaign cancelled`)
        return item
      },
    }),
    startAutomationSmsMarketingCampaign: build.mutation<
      unknown,
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, started_at, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/start-automated-campaign`,
          method: 'post',
          data: { started_at },
        }
      },
      invalidatesTags: (_item, _2, item) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: unknown) => {
        toast.success(`SMS campaign automation started`)
        return item
      },
    }),
    stopAutomationSmsMarketingCampaign: build.mutation<
      unknown,
      MarketingSmsCampaignType
    >({
      query: ({ organisation_id, ...rest }) => {
        return {
          url: `/${organisation_id}/marketing/sms-campaigns/${rest.id}/pause-automated-campaign`,
          method: 'post',
          data: {},
        }
      },
      invalidatesTags: (_item, _2, item) => [
        { type: 'SMSCampaigns', id: item?.id },
        { type: 'SMSCampaigns', id: 'LIST' },
      ],
      transformResponse: (item: unknown) => {
        toast.success(`SMS campaign automation stopped`)
        return item
      },
    }),

    getSmsMarketingCampaignReport: build.query<
      MarketingSMSCampaignReport,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/marketing/sms-campaigns/${id}/reports`,
        method: 'get',
      }),
    }),
    getSmsMarketingCampaignMessagesReport: build.query<
      ServicePaginatedResponseType<MarketingSMSCampaignMessage>,
      {
        orgId: string
        campaign_id: string
        page: number
        event_type?: SmsEventTypes
      }
    >({
      query: ({ orgId, campaign_id, page, event_type }) => ({
        url: buildUrl(
          `/${orgId}/marketing/sms-campaigns/${campaign_id}/sms-messages`,
          { page, event_type }
        ),
        method: 'get',
      }),
    }),
    getSmsMarketingCampaignErrorReport: build.query<
      ServicePaginatedResponseType<MarketingSmsCampaignError>,
      {
        orgId: string
        campaign_id: string
        page: number
      }
    >({
      query: ({ orgId, campaign_id, page }) => ({
        url: buildUrl(
          `/${orgId}/marketing/sms-campaigns/${campaign_id}/errors`,
          { page }
        ),
        method: 'get',
      }),
    }),
    getSmsMarketingCampaignEventsReport: build.query<
      ServicePaginatedResponseType<MarketingSMSCampaignEvent>,
      {
        orgId: string
        campaign_id: string
        page: number
        event_type?: SmsEventTypes
      }
    >({
      query: ({ orgId, campaign_id, page, event_type }) => ({
        url: buildUrl(
          `/${orgId}/marketing/sms-campaigns/${campaign_id}/events`,
          { page, event_type }
        ),
        method: 'get',
      }),
    }),

    getGsmConversion: build.mutation<
      { message: string },
      {
        orgId: string
        message: string
      }
    >({
      query: ({ orgId, message }) => ({
        url: `/${orgId}/marketing/sms-campaigns/convert-to-gsm`,
        method: 'post',
        data: {
          message,
        },
      }),
    }),

    /*
    SMS END
    */

    /**
     * ----
     * VOUCHER QUERIES START
     * ---
     */
    getVouchers: build.query<
      ServiceCursorPaginatedResponseType<VoucherType>,
      { orgId: string; page: string; limit: number }
    >({
      query: ({ orgId, page, limit }) => ({
        url: buildUrl(`/${orgId}/vouchers`, {
          cursor: page,
          limit,
          query_type: 'cursor',
        }),
        method: 'get',
      }),
      providesTags: [
        {
          id: 'LIST',
          type: 'Vouchers',
        },
      ],
    }),
    getVoucher: build.query<VoucherType, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/vouchers/${id}`,
        method: 'get',
      }),

      providesTags: (item: any) => [{ id: item?.id, type: 'Vouchers' }],
      transformResponse(data: { voucher: VoucherType }) {
        return data.voucher
      },
    }),
    getVoucherImports: build.query<
      ServiceCursorPaginatedResponseType<VoucherCodeImportType>,
      { orgId: string; voucher_id: string; page: string; limit: number }
    >({
      query: ({ orgId, voucher_id, page, limit }) => ({
        url: buildUrl(`/${orgId}/voucher-code-imports`, {
          voucher_id,
          cursor: page,
          limit,
          query_type: 'cursor',
        }),
        method: 'get',
      }),
      providesTags: ['Vouchers'],
    }),
    getVoucherImportsRows: build.query<
      ServiceCursorPaginatedResponseType<VoucherCodeImportRowsType>,
      { orgId: string; voucher_id: string; page: string; limit: number }
    >({
      query: ({ orgId, voucher_id, page, limit }) => ({
        url: buildUrl(`/${orgId}/voucher-code-imports/${voucher_id}/rows`, {
          cursor: page,
          limit,
          query_type: 'cursor',
        }),
        method: 'get',
      }),

      providesTags: ['Vouchers'],
    }),
    getVoucherCodes: build.query<
      ServiceCursorPaginatedResponseType<VoucherCodeType>,
      { orgId: string; code: string; page: string; limit: number }
    >({
      query: ({ orgId, code, page, limit }) => ({
        url: buildUrl(`/${orgId}/vouchers/${code}/codes`, {
          cursor: page,
          limit,
          query_type: 'cursor',
        }),
        method: 'get',
      }),

      providesTags: ['Vouchers'],
    }),
    getVoucherStatus: build.query<
      ServicePaginatedResponseType<VoucherStatusType>,
      { orgId: string; voucherId: string }
    >({
      query: ({ orgId, voucherId }) => ({
        url: `/${orgId}/vouchers/search-by-code?code=${voucherId}`,
        method: 'get',
      }),
      providesTags: ['Vouchers'],
      transformErrorResponse: (error: any) => {
        return error
      },
    }),
    getVoucherPerformanceReport: build.query<
      VoucherPerformanceReportType,
      { orgId: string; voucherId: string }
    >({
      query: ({ orgId, voucherId }) => ({
        url: `/${orgId}/vouchers/${voucherId}/performance-report`,
        method: 'get',
      }),
      providesTags: ['Vouchers'],
    }),
    createVoucher: build.mutation<
      VoucherType,
      { orgId: string; voucher: Partial<VoucherType> }
    >({
      query: ({ orgId, voucher }) => ({
        url: `/${orgId}/vouchers`,
        method: 'POST',
        data: {
          ...voucher,
        },
      }),

      invalidatesTags: () => [{ id: 'LIST', type: 'Vouchers' }],
    }),
    updateVoucher: build.mutation<
      VoucherType,
      { orgId: string; voucher: Partial<VoucherType> }
    >({
      query: ({ orgId, voucher }) => ({
        url: `/${orgId}/vouchers/${voucher.id}`,
        method: 'PUT',
        data: {
          ...voucher,
        },
      }),

      invalidatesTags: (item: any) =>
        item
          ? [
              { id: item?.id, type: 'Vouchers' },
              { id: 'LIST', type: 'Vouchers' },
            ]
          : [],
    }),
    postRevokeVoucherCode: build.mutation<
      { message: string },
      { orgId: string; code: string }
    >({
      query: ({ orgId, code }) => ({
        url: `/${orgId}/vouchers/revoke-voucher-code`,
        method: 'post',
        data: { code: code.replace(/-|\s/g, '') },
      }),
      invalidatesTags: () => ['Vouchers'],
    }),
    postRedeemVoucherCode: build.mutation<
      VoucherCodeResponseType,
      { orgId: string; code: string }
    >({
      query: ({ orgId, code }) => ({
        url: `/${orgId}/vouchers/redeem-voucher-code`,
        method: 'post',
        data: { code: code.replace(/-|\s/g, '') },
      }),
      invalidatesTags: () => ['Vouchers'],
    }),
    getVoucherImportOverview: build.query<
      RowsOverview,
      { orgId: string; voucherId: string }
    >({
      query: ({ orgId, voucherId }) => ({
        url: `/${orgId}/voucher-code-imports/${voucherId}/rows-overview`,
        method: 'get',
      }),
      providesTags: ['Vouchers'],
      transformResponse(res: { rows_overview: RowsOverview }) {
        return res.rows_overview
      },
    }),
    createVoucherCodeImport: build.mutation<
      VoucherCodeImportType,
      { orgId: string; voucherId: string; csvData: string; fileName: string }
    >({
      query: ({ orgId, voucherId, csvData, fileName }) => ({
        url: `/${orgId}/voucher-code-imports`,
        method: 'POST',
        data: {
          voucher_id: voucherId,
          csv_data: csvData,
          file_name: fileName,
        },
      }),
      invalidatesTags: ['Vouchers'],
      transformResponse(data: { voucher_code_import: VoucherCodeImportType }) {
        toast.success('Voucher codes import started successfully')
        return data.voucher_code_import
      },
      transformErrorResponse: () => {
        toast.error('Failed to start voucher codes import')
        return { error: 'Failed to start import' }
      },
    }),

    /**
     * ----
     * VOUCHER QUERIES END
     * ---
     */

    getSettings: build.query<
      ServiceSettingsType,
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/settings`,
        method: 'get',
      }),
      providesTags: [{ type: 'ServiceSettings', id: 'LIST' }],
      transformResponse(data: { data: ServiceSettingsType }) {
        return data.data
      },
    }),
    updateSettings: build.mutation<
      ServiceSettingsType,
      Partial<ServiceSettingsType>
    >({
      query: (req: any) => ({
        url: `/${req.organisation_id}/settings`,
        method: 'put',
        data: req,
      }),

      transformResponse(data: { setting: ServiceSettingsType }) {
        return data.setting
      },
      async onQueryStarted(req, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            serviceApi.util.updateQueryData(
              'getSettings',
              { orgId: req.organisation_id! },
              () => data
            )
          )
          toast.success('Settings updated')
        } catch {
          toast.error('Something went wrong, could not update settings')
        }
      },
    }),

    getSupportedDataProviders: build.query<
      SupportedDataProvidersResponse,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/marketing/supported-data-providers`,
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'SupportedDataProviders' }],
    }),

    getReportBuilder: build.query<
      EmailMarketingReportType,
      {
        orgId: string
        start: Date | null
        end: Date | null
        type: string
        segment_id?: string
      }
    >({
      query: ({
        orgId,
        start,
        end,
        type = 'email_campaigns_organisation_report',
        segment_id,
      }) => ({
        url: buildUrl(`/${orgId}/reports/latest`, {
          type,
          from: start && format(start, TimeFormats.BackendYearMonthDay),
          to: end && format(end, TimeFormats.BackendYearMonthDay),
          ...(segment_id && { segment_id }),
        }),
        method: 'GET',
      }),

      transformErrorResponse: async (
        rawError: any,
        _,
        { orgId, start, end, type, segment_id }
      ) => {
        const { response } = rawError as AxiosError<{ message: string }>
        console.log('[rpt] transformErrorResponse fired', {
          type,
          responseStatus: response?.status,
        })
        if (response?.status !== 403) {
          return response?.data
        }

        // Dedup concurrent POST /reports for the same args. Without this,
        // a pusher-driven refetch storm (or two parallel useReportBuilder
        // mounts) can fire many POSTs simultaneously, each creating a new
        // report on the server and amplifying load until the rate-limiter
        // 429s everything.
        const dedupKey = [
          orgId,
          type,
          start ? format(start, TimeFormats.BackendYearMonthDay) : '',
          end ? format(end, TimeFormats.BackendYearMonthDay) : '',
          segment_id ?? '',
        ].join('|')

        let inflight = inflightReportCreates.get(dedupKey)
        if (!inflight) {
          console.log('[rpt] transformErrorResponse POST /reports', {
            type,
            orgId,
            dedupKey,
          })
          inflight = post<{ data: EmailMarketingReportType }>(
            `/${orgId}/reports`,
            {
              type,
              ...(end && {
                to: format(end, TimeFormats.BackendYearMonthDay),
              }),
              ...(start && {
                from: format(start, TimeFormats.BackendYearMonthDay),
              }),
              ...(segment_id && { segment_id }),
            },
            null,
            'service'
          ).finally(() => {
            inflightReportCreates.delete(dedupKey)
          })
          inflightReportCreates.set(dedupKey, inflight)
        } else {
          console.log('[rpt] transformErrorResponse POST /reports (dedup)', {
            type,
            dedupKey,
          })
        }

        const resp = await inflight
        return resp.data
      },
      providesTags: () => [
        { id: 'LIST', type: 'EmailMarketingCampaignReport' },
      ],
    }),

    storeEmailCampaignEventsExport: build.mutation<
      {
        message: string
        data: { id: string }
      },
      {
        orgId: string
        emailCampaignId: string
        eventType: string | null
      }
    >({
      query: ({ orgId, emailCampaignId, eventType }) => ({
        url: `/${orgId}/email-campaign-events-exports`,
        method: 'POST',
        data: {
          email_campaign_id: emailCampaignId,
          event_type: eventType,
        },
      }),
    }),

    getSegmentDataExports: build.query<
      ServicePaginatedResponseType<SegmentDataExport>,
      { orgId: string; cursor?: string }
    >({
      query: ({ orgId, cursor }) => ({
        url: buildUrl(`/${orgId}/segment-data-exports`, {
          cursor,
        }),
        method: 'GET',
      }),
      providesTags: () => [{ id: 'LIST', type: 'SegmentDataExports' }],
    }),

    storeSegmentDataExport: build.mutation<
      { message: string; text: string },
      {
        orgId: string
        segment_id?: string | null
        data_provider?: string
      }
    >({
      query: ({ orgId, segment_id, data_provider }) => ({
        url: `/${orgId}/segment-data-exports`,
        method: 'POST',
        data: {
          segment_id,
          ...(data_provider && { data_provider }),
        },
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'SegmentDataExports' }],
    }),

    storeSegmentDataRemoval: build.mutation<
      { message: string; segment_data_removal_id: string },
      { orgId: string; segment_id: string; data_provider?: string }
    >({
      query: ({ orgId, segment_id, data_provider }) => ({
        url: `/${orgId}/segment-data-removals`,
        method: 'POST',
        data: {
          segment_id,
          ...(data_provider && { data_provider }),
        },
      }),
    }),

    refreshMarketingReport: build.mutation<
      EmailMarketingReportType,
      {
        orgId: string
        start: Date | null
        end: Date | null
        type: string
        segment_id?: string
      }
    >({
      query: ({
        orgId,
        start,
        end,
        type = 'email_campaigns_organisation_report',
        segment_id,
      }) => ({
        url: buildUrl(`/${orgId}/reports`, {
          type,
          from: start && format(start, TimeFormats.BackendYearMonthDay),
          to: end && format(end, TimeFormats.BackendYearMonthDay),
          ...(segment_id && { segment_id }),
        }),
        method: 'POST',
      }),

      invalidatesTags: () => [
        { id: 'LIST', type: 'EmailMarketingCampaignReport' },
      ],
    }),

    getWorkflows: build.query<
      ServicePaginatedResponseType<WorkflowType>,
      {
        orgId: string
        cursor: string | null | undefined
        name?: string
        serial?: string
        status?: string
      }
    >({
      query: ({ orgId, cursor, name, serial, status }) => ({
        url: buildUrl(`/${orgId}/workflows`, {
          cursor,
          name,
          serial,
          status,
        }),
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'Workflow' }],
    }),

    getWorkflowsMetadata: build.query<WorkflowMetadata, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/public/${orgId}/workflows/metadata`,
        method: 'get',
      }),
    }),

    getWorkflow: build.query<WorkflowType, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/workflows/${id}`,
        method: 'get',
      }),
      transformResponse: (x: { data: WorkflowType }) => {
        // php treats empty objects as [] instead of {}, this is a little
        // hack to fix that.
        if (x.data.nodes) {
          x.data.nodes = x.data.nodes.map((node: any) => {
            if (
              Array.isArray(node.properties) &&
              node.properties.length === 0
            ) {
              return { ...node, properties: {} }
            }
            return node
          })
        }

        return x.data
      },
      providesTags: (item: any) => [{ id: item?.id, type: 'Workflow' }],
    }),

    storeWorkflow: build.mutation<
      { data: WorkflowType },
      {
        orgId: string
        workflow: {
          name: string
          debounce?: number | null
          serials?: string[] | null
          start_node_x_position: number | null
          start_node_y_position: number | null
        }
      }
    >({
      query({ orgId, workflow }) {
        return {
          url: `/${orgId}/workflows`,
          method: 'POST',
          data: workflow,
        }
      },
      invalidatesTags: () => [{ id: 'LIST', type: 'Workflow' }],
    }),

    getWorkflowTemplates: build.query<
      { data: WorkflowTemplateSimple[] },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/workflows/templates`,
        method: 'get',
      }),
    }),

    getWorkflowTemplate: build.query<
      WorkflowTemplate,
      { orgId: string; key: string }
    >({
      query: ({ orgId, key }) => ({
        url: `/${orgId}/workflows/templates/${key}`,
        method: 'get',
      }),
      transformResponse: (x: { data: WorkflowTemplate }) => x.data,
    }),

    instantiateWorkflowFromTemplate: build.mutation<
      { data: WorkflowType },
      { orgId: string; payload: InstantiateWorkflowFromTemplateRequest }
    >({
      query: ({ orgId, payload }) => ({
        url: `/${orgId}/workflows/from-template`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'Workflow' }],
    }),

    updateWorkflow: build.mutation<
      { data: WorkflowType },
      {
        orgId: string
        workflowId: string
        data: {
          name?: string
          debounce?: number
          serials?: string[]
        }
      }
    >({
      query: ({ orgId, workflowId, data }) => ({
        url: `/${orgId}/workflows/${workflowId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            morpheusApi.util.invalidateTags([
              { type: 'Workflows', id: workflowId },
            ])
          )
        } catch {
          //
        }
      },
    }),

    activateWorkflow: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
      }
    >({
      query: ({ orgId, workflowId }) => ({
        url: `/${orgId}/workflows/${workflowId}/activate`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
    }),

    pauseWorkflow: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
      }
    >({
      query: ({ orgId, workflowId }) => ({
        url: `/${orgId}/workflows/${workflowId}/pause`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
    }),

    destroyWorkflow: build.mutation<
      { message: string },
      { orgId: string; workflowId: string }
    >({
      query({ orgId, workflowId }) {
        return {
          url: `/${orgId}/workflows/${workflowId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (result, error, { workflowId }) =>
        result
          ? [
              { id: 'LIST', type: 'Workflow' },
              { id: workflowId, type: 'Workflow' },
            ]
          : [],
    }),

    storeWorkflowSerial: build.mutation<
      { message: string },
      { orgId: string; workflowId: string; serial: string }
    >({
      query({ orgId, workflowId, serial }) {
        return {
          url: `/${orgId}/workflows/${workflowId}/serials`,
          method: 'POST',
          data: { serial },
        }
      },
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
    }),

    destroyWorkflowSerial: build.mutation<
      { data: WorkflowType },
      { orgId: string; workflowId: string; serial: string }
    >({
      query({ orgId, workflowId, serial }) {
        return {
          url: `/${orgId}/workflows/${workflowId}/serials`,
          method: 'DELETE',
          data: { serial },
        }
      },
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
    }),

    storeWorkflowTrigger: build.mutation<
      {
        data: WorkflowTrigger
      },
      {
        orgId: string
        workflowId: string
        trigger: string
        conditions: WorkflowTriggerCondition[]
      }
    >({
      query({ orgId, workflowId, trigger, conditions }) {
        return {
          url: `/${orgId}/workflows/${workflowId}/triggers`,
          method: 'POST',
          data: { trigger, conditions },
        }
      },
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        return invalidateMorpheusWorkflow(dispatch, workflowId, queryFulfilled)
      },
    }),

    updateWorkflowTrigger: build.mutation<
      { data: WorkflowTrigger },
      {
        orgId: string
        workflowId: string
        id: string
        // Replaces ALL existing conditions; send the full list to keep.
        conditions: WorkflowTriggerCondition[]
      }
    >({
      query({ orgId, workflowId, id, conditions }) {
        return {
          url: `/${orgId}/workflows/${workflowId}/triggers/${id}`,
          method: 'PUT',
          data: { conditions },
        }
      },
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        return invalidateMorpheusWorkflow(dispatch, workflowId, queryFulfilled)
      },
    }),

    destroyWorkflowTrigger: build.mutation<
      { data: WorkflowType },
      { orgId: string; workflowId: string; id: string }
    >({
      query({ orgId, workflowId, id }) {
        return {
          url: `/${orgId}/workflows/${workflowId}/triggers/${id}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        return invalidateMorpheusWorkflow(dispatch, workflowId, queryFulfilled)
      },
    }),

    updateWorkflowNodePosition: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
        workflowNodeId: string
        params: {
          x_position?: number
          y_position?: number
          label?: string
        }
      }
    >({
      query: ({ orgId, workflowId, workflowNodeId, params }) => ({
        url: `/${orgId}/workflows/${workflowId}/workflow-nodes/${workflowNodeId}/position`,
        method: 'PUT',
        data: params,
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            morpheusApi.util.invalidateTags([
              { type: 'Workflows', id: workflowId },
            ])
          )
        } catch {
          //
        }
      },
    }),

    updateWorkflowNodeProperties: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
        workflowNodeId: string
        params: {
          properties: Record<string, unknown>
        }
      }
    >({
      query: ({ orgId, workflowId, workflowNodeId, params }) => ({
        url: `/${orgId}/workflows/${workflowId}/workflow-nodes/${workflowNodeId}/properties`,
        method: 'PUT',
        data: params,
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            morpheusApi.util.invalidateTags([
              { type: 'Workflows', id: workflowId },
            ])
          )
        } catch {
          //
        }
      },
    }),

    storeWorkflowNode: build.mutation<
      { data: WorkflowNodeType },
      {
        orgId: string
        workflowId: string
        sourceWorkflowNodeId: string | null | undefined
        edgeLabel: string | null | undefined
        type: string
        x_position: number
        y_position: number
        label: string
        properties: Record<string, unknown>
      }
    >({
      query: ({
        orgId,
        workflowId,
        sourceWorkflowNodeId,
        edgeLabel,
        type,
        x_position,
        y_position,
        label,
        properties,
      }) => ({
        url: `/${orgId}/workflows/${workflowId}/workflow-nodes`,
        method: 'POST',
        data: {
          source_workflow_node_id: sourceWorkflowNodeId,
          edge_label: edgeLabel,
          type,
          x_position,
          y_position,
          label,
          properties,
        },
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            morpheusApi.util.invalidateTags([
              { type: 'Workflows', id: workflowId },
            ])
          )
        } catch {
          //
        }
      },
    }),

    deleteWorkflowNode: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
        workflowNodeId: string
      }
    >({
      query: ({ orgId, workflowId, workflowNodeId }) => ({
        url: `/${orgId}/workflows/${workflowId}/workflow-nodes/${workflowNodeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            morpheusApi.util.invalidateTags([
              { type: 'Workflows', id: workflowId },
            ])
          )
        } catch {
          //
        }
      },
    }),

    updateWorkflowStartNodePosition: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
        params: {
          start_node_x_position: number | null
          start_node_y_position: number | null
        }
      }
    >({
      query: ({ orgId, workflowId, params }) => ({
        url: `/${orgId}/workflows/${workflowId}/start-node-position`,
        method: 'PUT',
        data: params,
      }),
      invalidatesTags: (result, error, { workflowId }) =>
        result ? [{ id: workflowId, type: 'Workflow' }] : [],
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            morpheusApi.util.invalidateTags([
              { type: 'Workflows', id: workflowId },
            ])
          )
        } catch {
          //
        }
      },
    }),

    getWorkflowEmails: build.query<
      {
        data: WorkflowEmailSimple[]
      },
      {
        orgId: string
        workflowId: string
      }
    >({
      query: ({ orgId, workflowId }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails`,
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'WorkflowEmail' }],
    }),

    getWorkflowRuns: build.query<
      {
        data: WorkflowRunSimple[]
        links: {
          prev: string | null | undefined
          next: string | null | undefined
        }
        meta: {
          per_page: number
        }
      },
      {
        orgId: string
        workflowId: string
        cursor: string | null | undefined
        statuses?: string[]
      }
    >({
      query: ({ orgId, workflowId, cursor, statuses }) => {
        const searchParams = new URLSearchParams()
        if (cursor) {
          searchParams.append('cursor', cursor)
        }
        if (statuses && statuses.length > 0) {
          statuses.forEach((status) => {
            searchParams.append('status[]', status)
          })
        }
        const queryString = searchParams.toString()
        const url = `/${orgId}/workflows/${workflowId}/workflow-runs${queryString ? `?${queryString}` : ''}`
        return {
          url,
          method: 'get',
        }
      },
      providesTags: () => [{ id: 'LIST', type: 'WorkflowRun' }],
    }),

    getWorkflowRun: build.query<
      WorkflowRun,
      {
        orgId: string
        workflowId: string
        runId: string
      }
    >({
      query: ({ orgId, workflowId, runId }) => ({
        url: `/${orgId}/workflows/${workflowId}/workflow-runs/${runId}`,
        method: 'get',
      }),
      transformResponse: (response: { data: WorkflowRun }) => response.data,
      providesTags: (result, error, { runId }) => [
        { type: 'WorkflowRun', id: runId },
      ],
    }),

    getWorkflowLogs: build.query<
      ServiceCursorPaginatedResponseType<WorkflowLog>,
      {
        orgId: string
        workflowId: string
        cursor?: string | null | undefined
      }
    >({
      query: ({ orgId, workflowId, cursor }) => ({
        url: buildUrl(`/${orgId}/workflows/${workflowId}/logs`, {
          cursor: cursor ?? null,
        }),
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'WorkflowLogs' }],
    }),

    getWorkflowRunsReport: build.query<
      {
        total_runs: number
        status_counts: {
          pending: number
          waiting: number
          processing: number
          complete: number
          failed: number
          retrying: number
        }
      },
      { orgId: string; workflowId: string }
    >({
      query: ({ orgId, workflowId }) => ({
        url: `/${orgId}/workflows/${workflowId}/workflow-runs/report`,
        method: 'get',
      }),
      providesTags: () => [{ id: 'LIST', type: 'WorkflowRun' }],
    }),

    storeWorkflowRun: build.mutation<
      { data: WorkflowRun },
      {
        orgId: string
        workflowId: string
        orgRegId: string
      }
    >({
      query: ({ orgId, workflowId, orgRegId }) => ({
        url: `/${orgId}/workflows/${workflowId}/workflow-runs`,
        method: 'POST',
        data: { org_reg_id: orgRegId },
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'WorkflowRun' }],
    }),

    getWorkflowEmail: build.query<
      WorkflowEmail,
      { orgId: string; workflowId: string; workflowEmailId: string }
    >({
      query: ({ orgId, workflowId, workflowEmailId }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails/${workflowEmailId}`,
        method: 'get',
      }),
      transformResponse: (response: { data: WorkflowEmail }) => response.data,
      providesTags: (item: any) => [{ id: item?.id, type: 'WorkflowEmail' }],
    }),

    getWorkflowEmailById: build.query<
      WorkflowEmail,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        url: `/${orgId}/workflow-emails/${id}`,
        method: 'get',
      }),
      transformResponse: (response: { data: WorkflowEmail }) => response.data,
      providesTags: (item) => [{ id: item?.id, type: 'WorkflowEmail' }],
    }),

    /**
     * Search workflow emails by name across all of an org's workflows
     * (cursor-paginated). Distinct from `getWorkflowEmails`, which lists a
     * single workflow's emails — this is the cross-workflow org-wide search
     * used by pickers like `WorkflowEmailCombobox`.
     */
    searchWorkflowEmails: build.query<
      ServiceCursorPaginatedResponseType<WorkflowEmailSimple>,
      { orgId: string; name?: string; cursor?: string | null }
    >({
      query: ({ orgId, name, cursor }) => ({
        url: buildUrl(`/${orgId}/workflow-emails`, {
          name,
          cursor: cursor ?? null,
        }),
        method: 'get',
      }),
      providesTags: (result) => [
        ...(result?.data.map((email) => ({
          id: email.id,
          type: 'WorkflowEmail' as const,
        })) ?? []),
        { id: 'LIST', type: 'WorkflowEmail' },
      ],
    }),

    storeWorkflowEmail: build.mutation<
      { data: WorkflowEmail },
      {
        orgId: string
        workflowId: string
        workflowEmail: {
          type: string
          name: string
          email_json?: string | null | undefined
          email_html?: string | null | undefined
        }
      }
    >({
      query: ({ orgId, workflowId, workflowEmail }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails`,
        method: 'POST',
        data: workflowEmail,
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'WorkflowEmail' }],
    }),

    updateWorkflowEmail: build.mutation<
      { data: WorkflowEmail },
      {
        orgId: string
        workflowId: string
        workflowEmailId: string
        workflowEmail: Partial<WorkflowEmail>
      }
    >({
      query: ({ orgId, workflowId, workflowEmailId, workflowEmail }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails/${workflowEmailId}`,
        method: 'PUT',
        data: workflowEmail,
      }),
      invalidatesTags: (result, error, { workflowEmailId }) =>
        result
          ? [
              { id: workflowEmailId, type: 'WorkflowEmail' },
              { id: 'LIST', type: 'WorkflowEmail' },
            ]
          : [],
    }),

    destroyWorkflowEmail: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
        workflowEmailId: string
      }
    >({
      query: ({ orgId, workflowId, workflowEmailId }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails/${workflowEmailId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { workflowEmailId }) =>
        result
          ? [
              { id: workflowEmailId, type: 'WorkflowEmail' },
              { id: 'LIST', type: 'WorkflowEmail' },
            ]
          : [],
    }),

    sendWorkflowEmailPreview: build.mutation<
      { message: string; text: string },
      {
        orgId: string
        workflowId: string
        workflowEmailId: string
        from_id: string
        to_email: string
        to_name: string
      }
    >({
      query: ({
        orgId,
        workflowId,
        workflowEmailId,
        from_id,
        to_email,
        to_name,
      }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails/${workflowEmailId}/send-preview`,
        data: { from_id, to_email, to_name },
        method: 'post',
      }),
    }),

    getWorkflowEmailLinks: build.query<
      { links: string[] },
      { orgId: string; workflowId: string; workflowEmailId: string }
    >({
      query: ({ orgId, workflowId, workflowEmailId }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails/${workflowEmailId}/links`,
        method: 'get',
      }),
      providesTags: (_result, _error, { workflowEmailId }) => [
        { id: workflowEmailId, type: 'WorkflowEmail' },
      ],
    }),

    getWorkflowSmsMessages: build.query<
      {
        data: WorkflowSmsMessage[]
      },
      {
        orgId: string
        workflowId: string
      }
    >({
      query: ({ orgId, workflowId }) => ({
        url: `/${orgId}/workflows/${workflowId}/sms-messages`,
        method: 'GET',
      }),
      providesTags: (result: any) => [
        ...(result?.data?.map((item: any) => ({
          id: item?.id,
          type: 'WorkflowSmsMessage' as const,
        })) ?? []),
        { id: 'LIST', type: 'WorkflowSmsMessages' },
      ],
    }),

    createWorkflowSmsMessage: build.mutation<
      { data: WorkflowSmsMessage },
      {
        orgId: string
        workflowId: string
        data: CreateWorkflowSmsMessageRequest
      }
    >({
      query: ({ orgId, workflowId, data }) => ({
        url: `/${orgId}/workflows/${workflowId}/sms-messages`,
        method: 'POST',
        data,
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'WorkflowSmsMessages' }],
    }),

    getWorkflowSmsMessage: build.query<
      WorkflowSmsMessage,
      {
        orgId: string
        workflowId: string
        id: string
      }
    >({
      query: ({ orgId, workflowId, id }) => ({
        url: `/${orgId}/workflows/${workflowId}/sms-messages/${id}`,
        method: 'GET',
      }),
      providesTags: (result: any) => [
        { id: result?.id, type: 'WorkflowSmsMessage' },
      ],
    }),

    updateWorkflowSmsMessage: build.mutation<
      WorkflowSmsMessage,
      {
        orgId: string
        workflowId: string
        id: string
        data: UpdateWorkflowSmsMessageRequest
      }
    >({
      query: ({ orgId, workflowId, id, data }) => ({
        url: `/${orgId}/workflows/${workflowId}/sms-messages/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) =>
        result
          ? [
              { id, type: 'WorkflowSmsMessage' },
              { id: 'LIST', type: 'WorkflowSmsMessages' },
            ]
          : [],
    }),

    deleteWorkflowSmsMessage: build.mutation<
      { message: string },
      {
        orgId: string
        workflowId: string
        id: string
      }
    >({
      query: ({ orgId, workflowId, id }) => ({
        url: `/${orgId}/workflows/${workflowId}/sms-messages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        result
          ? [
              { id, type: 'WorkflowSmsMessage' },
              { id: 'LIST', type: 'WorkflowSmsMessages' },
            ]
          : [],
    }),

    getWorkflowEmailEvents: build.query<
      ServiceCursorPaginatedResponseType<EmailEventType>,
      {
        orgId: string
        workflowId: string
        emailId: string
        cursor?: string | null | undefined
      }
    >({
      query: ({ orgId, workflowId, emailId, cursor }) => ({
        url: buildUrl(
          `/${orgId}/workflows/${workflowId}/emails/${emailId}/events`,
          {
            cursor: cursor ?? null,
          }
        ),
        method: 'GET',
      }),
    }),

    getWorkflowEmailEventsSummary: build.query<
      {
        summary: {
          event_type: string
          count: number
        }[]
      },
      {
        orgId: string
        workflowId: string
        emailId: string
      }
    >({
      query: ({ orgId, workflowId, emailId }) => ({
        url: `/${orgId}/workflows/${workflowId}/emails/${emailId}/events/summary`,
        method: 'GET',
      }),
    }),

    getWorkflowSeeders: build.query<
      ServiceCursorPaginatedResponseType<WorkflowSeeder>,
      {
        orgId: string
        workflowId: string
        cursor?: string | null | undefined
      }
    >({
      query: ({ orgId, workflowId, cursor }) => ({
        url: buildUrl(`/${orgId}/workflows/${workflowId}/seeders`, {
          cursor: cursor ?? null,
        }),
        method: 'GET',
      }),
      providesTags: () => [{ id: 'LIST', type: 'Workflow' }],
    }),

    storeWorkflowSeederV2: build.mutation<
      {
        message: string
        workflow_seeder: WorkflowSeeder
      },
      {
        orgId: string
        workflowId: string
        segment_provider: string
        segment_id?: string | null
      }
    >({
      query: ({ orgId, workflowId, segment_provider, segment_id }) => ({
        url: `/${orgId}/workflows/${workflowId}/seeders/v2`,
        method: 'POST',
        data: {
          segment_provider,
          segment_id: segment_id ?? null,
        },
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'Workflow' }],
    }),

    getWorkflowSeeder: build.query<
      WorkflowSeeder,
      {
        orgId: string
        workflowId: string
        id: string
      }
    >({
      query: ({ orgId, workflowId, id }) => ({
        url: `/${orgId}/workflows/${workflowId}/seeders/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: WorkflowSeeder }) => response.data,
      providesTags: (result, error, { id }) => [{ id, type: 'Workflow' }],
    }),

    getWorkflowSeederLogs: build.query<
      ServiceCursorPaginatedResponseType<WorkflowSeederLog>,
      {
        orgId: string
        workflowId: string
        id: string
        cursor?: string | null | undefined
      }
    >({
      query: ({ orgId, workflowId, id, cursor }) => ({
        url: buildUrl(`/${orgId}/workflows/${workflowId}/seeders/${id}/logs`, {
          cursor: cursor ?? null,
        }),
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [{ id, type: 'Workflow' }],
    }),

    getWorkflowSeederResults: build.query<
      ServiceCursorPaginatedResponseType<WorkflowSeederResult>,
      {
        orgId: string
        workflowId: string
        id: string
        cursor?: string | null | undefined
      }
    >({
      query: ({ orgId, workflowId, id, cursor }) => ({
        url: buildUrl(
          `/${orgId}/workflows/${workflowId}/seeders/${id}/results`,
          {
            cursor: cursor ?? null,
          }
        ),
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [{ id, type: 'Workflow' }],
    }),

    explainWorkflow: build.mutation<
      { description: string },
      { orgId: string; workflowId: string }
    >({
      query: ({ orgId, workflowId }) => ({
        url: `/${orgId}/workflows/${workflowId}/explain`,
        method: 'POST',
      }),
    }),
  }),
})

export default serviceApi
